import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ChevronLeft, ChevronRight, Calendar, Clock, Package, Users, User, X, Check, CreditCard, Mail, Phone, Link2, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import TimeSlotSelector from '../components/TimeSlotSelector';
import DatePicker from '../components/DateRangePicker';
import ServiceCalendarPicker from '../components/ServiceCalendarPicker';
import SharePaymentLinkModal from '../components/SharePaymentLinkModal';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import api from '../utils/api';
import { BACKEND_URL } from '../config/api';
import { getTodayLocal, addDaysToDateString } from '../utils/dateUtils';
import { getTimezoneAbbr } from '../utils/timezones';

// Expandable description component for add-ons
const ExpandableAddOnDescription = ({ description, maxLength = 80 }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  if (!description) return null;
  
  const shouldTruncate = description.length > maxLength;
  const displayText = shouldTruncate && !isExpanded 
    ? description.substring(0, maxLength).trim() + '...'
    : description;
  
  return (
    <div className="mt-1">
      <p className="text-sm text-gray-600">
        {displayText}
      </p>
      {shouldTruncate && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
          className="text-xs font-medium text-blue-600 hover:text-blue-700 mt-1 flex items-center gap-1"
        >
          {isExpanded ? (
            <>See less <ChevronUp className="w-3 h-3" /></>
          ) : (
            <>See more <ChevronDown className="w-3 h-3" /></>
          )}
        </button>
      )}
    </div>
  );
};

// Currency formatting utility - supports all currencies properly
const formatCurrency = (amount, currency = 'USD') => {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0);
  } catch (e) {
    // Fallback for invalid currency codes
    return `${currency || '$'} ${(amount || 0).toFixed(2)}`;
  }
};

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#9e2146',
    },
  },
};

const AddBookingWizard = ({ assets, teamMembers, roles, onComplete, onCancel }) => {
  return (
    <Elements stripe={stripePromise}>
      <AddBookingWizardForm 
        assets={assets} 
        teamMembers={teamMembers} 
        roles={roles} 
        onComplete={onComplete} 
        onCancel={onCancel} 
      />
    </Elements>
  );
};

const AddBookingWizardForm = ({ assets, teamMembers, roles, onComplete, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [currentStep, setCurrentStep] = useState(1);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdBooking, setCreatedBooking] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showShareLinkModal, setShowShareLinkModal] = useState(false);
  const [paymentLinkData, setPaymentLinkData] = useState(null);
  
  // NEW: Load services
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [loadingServices, setLoadingServices] = useState(false);
  
  // Get organization timezone from settings (source of truth)
  const orgTimezone = localStorage.getItem('orgTimezone') || 'Asia/Dubai';
  
  // Use centralized timezone abbreviation utility
  const getTimezoneAbbreviation = (tz) => {
    return getTimezoneAbbr(tz);
  };
  
  const [formData, setFormData] = useState({
    serviceId: '',  // NEW: Required field
    serviceName: '',  // NEW: For display
    date: getTodayLocal(),
    startTime: '09:00',
    endTime: '11:00',
    duration: 120,
    groupSize: 1,
    selectedDurationIndex: null,
    selectedAddOns: {}, // {addonId: {title, price, type, description}}
    chargeAmount: '', // Editable amount for partial payments
    assetId: '',
    teamAssignments: [],
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    pickupLocation: '',
    paymentStatus: '', // No default - user must select
    totalAmount: 0,
    paymentMethod: '',
    timezone: orgTimezone,
    contactInfoConfirmed: false,  // NEW: Confirmation checkbox
    // Stripe card details
    cardNumber: '',
    cardExpiry: '',
    cardCvc: '',
    cardName: '',
    // Multi-currency support
    presentmentCurrency: '', // Customer's preferred currency (empty = service default)
  });

  const totalSteps = 6; // Updated: 1=Service, 2=Date/Time/Capacity, 3=Add-ons, 4=Customer, 5=Review, 6=Payment
  
  // Entry point selection - now includes 'custom' for custom bookings
  const [entryPoint, setEntryPoint] = useState('service'); // 'service', 'date', or 'custom'
  
  // Custom booking state
  const [customBookingData, setCustomBookingData] = useState({
    serviceName: '',
    serviceDescription: '',
    basePrice: '',
    notes: '',
    customAddOns: [], // [{description: string, amount: number}]
  });
  const [allOrgAddOns, setAllOrgAddOns] = useState([]); // Add-ons from all services for custom bookings
  
  // Multi-currency support state
  const [supportedCurrencies, setSupportedCurrencies] = useState([]);
  const [convertedPrice, setConvertedPrice] = useState(null);
  const [loadingConversion, setLoadingConversion] = useState(false);
  
  // Load services on mount
  useEffect(() => {
    loadServices();
    loadSupportedCurrencies();
  }, []);
  
  // Load supported currencies for multi-currency checkout
  const loadSupportedCurrencies = async () => {
    try {
      // Pass organization country for proper default currency
      const orgCountry = localStorage.getItem('orgCountry') || 'US';
      const response = await api.get(`/admin/supported-currencies?org_country=${orgCountry}`);
      if (response.data?.currencies) {
        // Sort to put guaranteed currencies first
        const sorted = [...response.data.currencies].sort((a, b) => {
          if (a.tier === 'guaranteed' && b.tier !== 'guaranteed') return -1;
          if (a.tier !== 'guaranteed' && b.tier === 'guaranteed') return 1;
          return 0;
        });
        setSupportedCurrencies(sorted);
      }
    } catch (error) {
      console.error('Failed to load supported currencies:', error);
    }
  };
  
  // Convert price when currency changes
  const convertPrice = async (toCurrency) => {
    if (!selectedService || !toCurrency) {
      setConvertedPrice(null);
      return;
    }
    
    const serviceCurrency = selectedService.currency || 'USD';
    if (toCurrency.toLowerCase() === serviceCurrency.toLowerCase()) {
      setConvertedPrice(null);
      return;
    }
    
    setLoadingConversion(true);
    try {
      // Calculate total amount
      const basePrice = selectedService.price || 0;
      const addOnsTotal = Object.values(formData.selectedAddOns || {}).reduce((sum, addon) => sum + (addon?.price || 0), 0);
      const preTotal = basePrice + addOnsTotal;
      const platformFee = (preTotal * 0.059) + 0.30;
      // Check both taxEnabled and salesTaxEnabled (different naming conventions)
      const taxEnabled = selectedService.taxEnabled || selectedService.salesTaxEnabled || false;
      let taxRateRaw = selectedService.taxRate || selectedService.salesTaxRate || 0;
      // CRITICAL: taxRate is stored as percentage (8.25), convert to decimal (0.0825)
      const taxRateDecimal = taxRateRaw > 1 ? taxRateRaw / 100 : taxRateRaw;
      const salesTax = taxEnabled ? preTotal * taxRateDecimal : 0;
      const total = preTotal + platformFee + salesTax;
      
      const response = await api.post('/admin/convert-price', {
        amount: total,
        fromCurrency: serviceCurrency,
        toCurrency: toCurrency
      });
      
      if (response.data) {
        setConvertedPrice({
          ...response.data,
          basePrice: basePrice,
          basePriceConverted: basePrice * response.data.rateWithFee
        });
      }
    } catch (error) {
      console.error('Failed to convert price:', error);
      setConvertedPrice(null);
    } finally {
      setLoadingConversion(false);
    }
  };
  
  // Trigger conversion when currency changes
  useEffect(() => {
    if (formData.presentmentCurrency && currentStep === 6) {
      convertPrice(formData.presentmentCurrency);
    }
  }, [formData.presentmentCurrency, currentStep]);
  
  // Load all org add-ons when custom mode is selected
  useEffect(() => {
    if (entryPoint === 'custom' && services.length > 0) {
      // Collect unique add-ons from all services
      const addOnsMap = new Map();
      services.forEach(service => {
        if (service.addOns && Array.isArray(service.addOns)) {
          service.addOns.forEach(addon => {
            const key = addon.title || addon.name;
            if (key && !addOnsMap.has(key)) {
              addOnsMap.set(key, {
                id: addon.id || `addon-${key}`,
                title: addon.title || addon.name,
                description: addon.description || '',
                price: addon.price || 0,
                type: addon.type || 'checkbox',
                fromService: service.name
              });
            }
          });
        }
      });
      setAllOrgAddOns(Array.from(addOnsMap.values()));
    }
  }, [entryPoint, services]);
  
  // Track expanded descriptions
  const [expandedServices, setExpandedServices] = useState({});
  
  // Track if we're intentionally selecting a service (not navigating back)
  const [isSelectingService, setIsSelectingService] = useState(false);
  
  // Track if we just selected a time (for auto-advance)
  const [justSelectedTime, setJustSelectedTime] = useState(false);
  
  // Track sub-view within step 2 (calendar → time slots with inline capacity)
  const [step2SubView, setStep2SubView] = useState('calendar'); // 'calendar', 'timeSlots'
  
  // Real-time availability state
  const [availabilityData, setAvailabilityData] = useState(null);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [availabilityReady, setAvailabilityReady] = useState(false);
  
  // Fetch real-time availability when date changes - PRE-FETCH before showing time slots
  useEffect(() => {
    const fetchAvailability = async () => {
      if (!selectedService?.id || !formData.date) {
        setAvailabilityData(null);
        setAvailabilityReady(false);
        return;
      }
      
      // Start loading immediately
      setAvailabilityLoading(true);
      setAvailabilityReady(false);
      
      try {
        const orgId = localStorage.getItem('organizationId');
        const formDataPayload = new FormData();
        formDataPayload.append('date', formData.date);
        formDataPayload.append('org_id', orgId);
        
        const response = await api.post(
          `/public/services/${selectedService.id}/availability-slots`,
          formDataPayload,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        
        setAvailabilityData(response.data);
        // Debug logging removed for production
        // console.log('[AddBookingWizard] Real-time availability:', response.data);
      } catch (error) {
        console.error('[AddBookingWizard] Failed to fetch availability:', error);
        setAvailabilityData(null);
      } finally {
        setAvailabilityLoading(false);
        setAvailabilityReady(true);
      }
    };
    
    // Fetch immediately when date is selected (not just when viewing time slots)
    if (formData.date && selectedService?.id) {
      fetchAvailability();
    }
  }, [selectedService?.id, formData.date]);
  
  // Auto-advance to step 2 when service is selected in step 1
  useEffect(() => {
    if (currentStep === 1 && formData.serviceId && entryPoint === 'service' && isSelectingService) {
      // Small delay to ensure state is fully updated
      const timer = setTimeout(() => {
        setCurrentStep(2);
        setStep2SubView('calendar'); // Reset to calendar view
        setIsSelectingService(false); // Reset flag
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [formData.serviceId, currentStep, entryPoint, isSelectingService]);
  
  // Calculate dynamic progress within Step 2
  const getStep2Progress = () => {
    if (currentStep !== 2) return currentStep;
    // Step 2 has 2 sub-views, advance progress as user makes selections
    if (step2SubView === 'calendar') return 2.0;
    if (step2SubView === 'timeSlots' && !formData.startTime) return 2.25;
    if (step2SubView === 'timeSlots' && formData.startTime && (!formData.groupSize || formData.groupSize < 1)) return 2.5;
    if (step2SubView === 'timeSlots' && formData.startTime && formData.groupSize >= 1) return 2.75;
    return 2;
  };
  
  const toggleServiceDescription = (serviceId) => {
    setExpandedServices(prev => ({
      ...prev,
      [serviceId]: !prev[serviceId]
    }));
  };
  
  const loadServices = async () => {
    try {
      setLoadingServices(true);
      const response = await api.get('/services');
      // Backend returns array directly, not nested in .services
      setServices(response.data || []);
    } catch (error) {
      console.error('Error loading services:', error);
      toast.error('Failed to load services');
    } finally {
      setLoadingServices(false);
    }
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const nextStep = () => {
    if (validateCurrentStep()) {
      // For custom bookings, skip step 2 (date/time/capacity) - it's already in step 1
      if (entryPoint === 'custom' && currentStep === 1) {
        setCurrentStep(3); // Skip to Add-ons
      } else {
        setCurrentStep(prev => Math.min(prev + 1, totalSteps));
      }
    }
  };

  const prevStep = () => {
    // For custom bookings, skip step 2 when going back
    if (entryPoint === 'custom' && currentStep === 3) {
      setCurrentStep(1); // Go back to custom form
    } else {
      setCurrentStep(prev => Math.max(prev - 1, 1));
    }
  };

  const validateCurrentStep = () => {
    switch (currentStep) {
      case 1:
        // Step 1: Service selection or Custom booking details
        if (entryPoint === 'custom') {
          // Custom booking validation
          if (!customBookingData.serviceName?.trim()) {
            toast.error('Please enter a service/item description');
            return false;
          }
          if (!customBookingData.basePrice || parseFloat(customBookingData.basePrice) <= 0) {
            toast.error('Please enter a valid price');
            return false;
          }
          if (!formData.date) {
            toast.error('Please select a date');
            return false;
          }
          if (!formData.startTime) {
            toast.error('Please select a time');
            return false;
          }
          return true;
        } else {
          // Regular service selection validation
          if (!formData.serviceId) {
            toast.error('Please select a service');
            return false;
          }
          return true;
        }
      case 2:
        // Step 2: Date, time, and capacity (skip for custom - already done in step 1)
        if (entryPoint === 'custom') {
          return true; // Custom bookings handle this in step 1
        }
        if (!formData.date || !formData.startTime || !formData.endTime) {
          toast.error('Please select date and time');
          return false;
        }
        if (!formData.groupSize || formData.groupSize < 1) {
          toast.error('Please select number of guests');
          return false;
        }
        // Validate party size against service capacity
        if (selectedService?.capacity && formData.groupSize > selectedService.capacity) {
          toast.error(`Maximum capacity is ${selectedService.capacity} guests. Please reduce group size or book multiple trips.`);
          return false;
        }
        return true;
      case 3:
        // Step 3: Add-ons (optional, always valid)
        return true;
      case 4:
        // Step 4: Customer info
        if (!formData.customerName || !formData.customerName.trim()) {
          toast.error('Customer name is required');
          return false;
        }
        if (!formData.customerEmail || !formData.customerEmail.trim()) {
          toast.error('Customer email is required');
          return false;
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.customerEmail)) {
          toast.error('Please enter a valid email address');
          return false;
        }
        if (!formData.customerPhone || !formData.customerPhone.trim()) {
          toast.error('Customer phone is required');
          return false;
        }
        return true;
      case 5:
        // Step 5: Review - no validation needed, payment selection optional
        return true;
      case 6:
        // Step 6: Payment - must select payment status
        if (!formData.paymentStatus) {
          toast.error('Please select a payment option');
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      // Validate required fields
      if (!formData.startTime) {
        toast.error('Missing time information');
        setIsSubmitting(false);
        return;
      }
      
      // For custom bookings, validate custom fields
      const isCustomBooking = entryPoint === 'custom';
      if (isCustomBooking) {
        if (!customBookingData.serviceName?.trim()) {
          toast.error('Please enter a service/item description');
          setIsSubmitting(false);
          return;
        }
        if (!customBookingData.basePrice || parseFloat(customBookingData.basePrice) <= 0) {
          toast.error('Please enter a valid price');
          setIsSubmitting(false);
          return;
        }
      }
      
      // Handle payment based on selected option - include full add-on details (title, price, etc.)
      const addOnsArray = Object.values(formData.selectedAddOns).map(addon => ({
        id: addon.id,
        title: addon.title,
        description: addon.description || '',
        price: addon.price,
        type: addon.type || 'checkbox'
      }));
      
      // For custom bookings, also include custom add-ons
      const customAddOnsArray = isCustomBooking 
        ? customBookingData.customAddOns.map((addon, idx) => ({
            id: `custom_addon_${idx}`,
            title: addon.description,
            description: addon.description,
            price: addon.amount,
            type: 'custom'
          }))
        : [];
      
      const allAddOns = [...addOnsArray, ...customAddOnsArray];
      const addOnsTotal = allAddOns.reduce((sum, addon) => sum + (addon.price || 0), 0);

      // Build booking data - handle custom vs regular
      const bookingData = isCustomBooking ? {
        // Custom booking fields
        isCustomBooking: true,
        bookingSource: 'internal-custom',
        serviceId: null,
        serviceName: customBookingData.serviceName,
        servicePrice: parseFloat(customBookingData.basePrice) || 0,
        preferredDate: formData.date,
        preferredTime: formData.startTime,
        duration: formData.duration || 60,
        customerName: formData.customerName,
        customerEmail: formData.customerEmail,
        customerPhone: formData.customerPhone,
        partySize: formData.groupSize,
        pickupLocation: formData.pickupLocation || '',
        notes: customBookingData.notes || '',
        selectedAddOns: allAddOns,
        customAddOns: customBookingData.customAddOns,
        addOnsTotal: addOnsTotal,
        paymentStatus: formData.paymentStatus,
        isInternalBooking: true,
        currency: 'USD'
      } : {
        // Regular booking fields
        serviceId: formData.serviceId,
        serviceName: formData.serviceName,
        servicePrice: selectedService?.price || 0,
        preferredDate: formData.date,
        preferredTime: formData.startTime,
        customerName: formData.customerName,
        customerEmail: formData.customerEmail,
        customerPhone: formData.customerPhone,
        partySize: formData.groupSize,
        pickupLocation: formData.pickupLocation || '',
        selectedAddOns: addOnsArray,
        addOnsTotal: addOnsTotal,
        paymentStatus: formData.paymentStatus,
        isInternalBooking: true,
        presentmentCurrency: formData.presentmentCurrency || ''
      };

      // For "Pay Now" or "Share Payment Link", create Stripe Checkout session
      if (formData.paymentStatus === 'pay_now_redirect' || formData.paymentStatus === 'send_link') {
        // Calculate pricing for modal
        const base_price = isCustomBooking 
          ? parseFloat(customBookingData.basePrice) || 0 
          : parseFloat(selectedService?.price || 0);
        const add_ons_total = addOnsTotal;
        const pre_total = base_price + add_ons_total;
        const platform_fee = (pre_total * 0.059) + 0.30;
        const tax_enabled = isCustomBooking ? true : (selectedService?.taxEnabled || selectedService?.salesTaxEnabled || false);
        let tax_rate_raw = isCustomBooking ? 8.25 : (selectedService?.taxRate || selectedService?.salesTaxRate || 0);
        const tax_rate_decimal = tax_rate_raw > 1 ? tax_rate_raw / 100 : tax_rate_raw;
        const sales_tax = tax_enabled ? pre_total * tax_rate_decimal : 0;
        
        const response = await api.post('/admin/bookings/create-with-checkout', bookingData);
        
        if (response.data.success) {
          const checkoutUrl = response.data.checkoutUrl;
          
          if (formData.paymentStatus === 'pay_now_redirect') {
            // Redirect admin to Stripe to enter card
            toast.success('Opening Stripe payment page...');
            // Open in same window for PWA/app compatibility
            window.location.href = checkoutUrl;
            return; // Don't call onComplete since we're navigating away
          } else if (formData.paymentStatus === 'send_link') {
            // Show share link modal
            setPaymentLinkData({
              checkoutUrl: checkoutUrl,
              customerEmail: formData.customerEmail,
              customerPhone: formData.customerPhone,
              bookingDetails: {
                serviceName: formData.serviceName,
                preferredDate: formData.date,
                preferredTime: formData.startTime,
                customerName: formData.customerName,
                totalAmount: (pre_total + platform_fee + sales_tax).toFixed(2)
              }
            });
            setShowShareLinkModal(true);
          }
          return;
        } else {
          throw new Error(response.data.message || 'Failed to create payment link');
        }
      }
      
      // For "Already Paid" or "Will Pay Later"
      if (formData.paymentStatus === 'paid' || formData.paymentStatus === 'unpaid') {
        const response = await api.post('/admin/bookings/create', bookingData);
        
        if (response.data.success) {
          toast.success('Booking created successfully!');
          
          // Notify setup guide of data change
          window.dispatchEvent(new Event('setupDataChanged'));
          
          setCreatedBooking({
            ...bookingData,
            ...response.data,
            confirmationCode: response.data.confirmationCode
          });
          setShowSuccessModal(true);
          return;
        } else {
          throw new Error(response.data.message || 'Booking creation failed');
        }
      }
      
      toast.error('Please select a payment option');
      setIsSubmitting(false);
    } catch (error) {
      console.error('=== ERROR IN HANDLE SUBMIT ===');
      console.error('Error:', error);
      console.error('Error response:', error.response?.data);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to create booking';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        // NEW: Service Selection Step with THREE entry points
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Package className="w-5 h-5" />
              Start Your Booking
            </h3>
            
            {/* Entry Point Selector - Service First, Date First, or Custom */}
            <div className="flex gap-2 mb-6">
              <button
                onClick={() => setEntryPoint('service')}
                className={`flex-1 py-2.5 px-3 rounded-lg font-medium transition-all text-sm ${
                  entryPoint === 'service'
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <Package className="w-4 h-4 inline mr-1.5" />
                Service
              </button>
              <button
                onClick={() => setEntryPoint('date')}
                className={`flex-1 py-2.5 px-3 rounded-lg font-medium transition-all text-sm ${
                  entryPoint === 'date'
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <Calendar className="w-4 h-4 inline mr-1.5" />
                Date
              </button>
              <button
                onClick={() => setEntryPoint('custom')}
                className={`flex-1 py-2.5 px-3 rounded-lg font-medium transition-all text-sm ${
                  entryPoint === 'custom'
                    ? 'bg-purple-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <CreditCard className="w-4 h-4 inline mr-1.5" />
                Custom
              </button>
            </div>
            
            {/* Custom Booking Flow */}
            {entryPoint === 'custom' && (
              <div className="space-y-4">
                {/* Custom Service Name */}
                <div>
                  <Label>Service/Item Description <span className="text-red-500">*</span></Label>
                  <Input
                    value={customBookingData.serviceName}
                    onChange={(e) => setCustomBookingData({...customBookingData, serviceName: e.target.value})}
                    placeholder="e.g., BBQ Tray + Sparkling Water, 10 Boat Charter..."
                    className="mt-1"
                  />
                </div>
                
                {/* Base Price */}
                <div>
                  <Label>Price <span className="text-red-500">*</span></Label>
                  <div className="relative mt-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={customBookingData.basePrice}
                      onChange={(e) => setCustomBookingData({...customBookingData, basePrice: e.target.value})}
                      placeholder="0.00"
                      className="pl-7"
                    />
                  </div>
                </div>
                
                {/* Date & Time */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Date <span className="text-red-500">*</span></Label>
                    <Input
                      type="date"
                      value={formData.date}
                      onChange={(e) => updateFormData('date', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Time <span className="text-red-500">*</span></Label>
                    <Input
                      type="time"
                      value={formData.startTime}
                      onChange={(e) => updateFormData('startTime', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                </div>
                
                {/* Duration and Party Size */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Duration</Label>
                    <select
                      value={formData.duration || 60}
                      onChange={(e) => updateFormData('duration', parseInt(e.target.value))}
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      <option value={15}>15 min</option>
                      <option value={30}>30 min</option>
                      <option value={45}>45 min</option>
                      <option value={60}>1 hour</option>
                      <option value={90}>1.5 hours</option>
                      <option value={120}>2 hours</option>
                      <option value={180}>3 hours</option>
                      <option value={240}>4 hours</option>
                      <option value={300}>5 hours</option>
                      <option value={360}>6 hours</option>
                      <option value={480}>8 hours</option>
                      <option value={720}>12 hours</option>
                      <option value={1440}>24 hours</option>
                    </select>
                  </div>
                  <div>
                    <Label>Party Size</Label>
                    <Input
                      type="number"
                      min="1"
                      value={formData.groupSize}
                      onChange={(e) => updateFormData('groupSize', parseInt(e.target.value) || 1)}
                      className="mt-1"
                    />
                  </div>
                </div>
                
                {/* Notes */}
                <div>
                  <Label>Notes</Label>
                  <textarea
                    value={customBookingData.notes}
                    onChange={(e) => setCustomBookingData({...customBookingData, notes: e.target.value})}
                    placeholder="Additional details about this booking..."
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-purple-500"
                    rows={3}
                  />
                </div>
              </div>
            )}
            
            {/* Service First Flow */}
            {entryPoint === 'service' && (
              <>
                {loadingServices ? (
              <div className="text-center py-12">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-600" />
                <p className="text-gray-500 mt-2">Loading services...</p>
              </div>
            ) : services.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <Package className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                <p className="text-gray-600 font-medium">No services available</p>
                <p className="text-sm text-gray-500 mt-1">Create a service first</p>
              </div>
            ) : (
              <div className="grid gap-3">
                {services.filter(s => s.active).map((service) => {
                  const isExpanded = expandedServices[service.id] || false;
                  const shouldTruncate = service.description && service.description.length > 100;
                  const displayDescription = shouldTruncate && !isExpanded 
                    ? service.description.substring(0, 100) + '...' 
                    : service.description;
                  
                  // Get main image from service
                  const mainImage = service.images && service.images.length > 0 
                    ? service.images[service.mainImageIndex || 0] 
                    : null;
                  
                  return (
                    <div
                      key={service.id}
                      onClick={() => {
                        setSelectedService(service);
                        updateFormData('serviceId', service.id);
                        updateFormData('serviceName', service.name);
                        updateFormData('duration', service.duration || 120);
                        updateFormData('totalAmount', service.price || 0);
                        setIsSelectingService(true); // Set flag to trigger auto-advance
                      }}
                      className={`p-4 border rounded-lg cursor-pointer transition-all ${
                        formData.serviceId === service.id
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        {/* Service Image */}
                        {mainImage ? (
                          <img 
                            src={mainImage} 
                            alt={service.name}
                            className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
                          />
                        ) : (
                          <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Package className="w-8 h-8 text-blue-600" />
                          </div>
                        )}
                        
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-gray-900">{service.name}</h4>
                          {service.description && (
                            <div className="mt-1">
                              <p className="text-sm text-gray-600">
                                {displayDescription}
                              </p>
                              {shouldTruncate && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleServiceDescription(service.id);
                                  }}
                                  className="text-xs text-blue-600 hover:text-blue-700 font-medium mt-1"
                                >
                                  {isExpanded ? 'See less' : 'See more'}
                                </button>
                              )}
                            </div>
                          )}
                          <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {service.duration || 120} min
                            </span>
                            <span className="flex items-center gap-1">
                              <Users className="w-4 h-4" />
                              Max {(() => {
                                // For per-asset: show min capacity of linked assets
                                // For per-ticket: show total capacity
                                const isPerAsset = service?.bookingMode === 'exclusive' || service?.capacityMode === 'per_asset';
                                const linkedAssetIds = service?.linkedAssets || service?.assetIds || [];
                                if (isPerAsset && linkedAssetIds.length > 0 && assets?.length > 0) {
                                  const linkedAssetDetails = assets.filter(a => linkedAssetIds.includes(a.id));
                                  if (linkedAssetDetails.length > 0) {
                                    return Math.min(...linkedAssetDetails.map(a => a.capacity || 12));
                                  }
                                }
                                return service.capacity || 12;
                              })()}
                            </span>
                          </div>
                        </div>
                        
                        <div className="flex flex-col items-end gap-2 flex-shrink-0">
                          <div className="text-xl font-bold text-blue-600">
                            {formatCurrency(service.price, service.currency)}
                          </div>
                          {formData.serviceId === service.id && (
                            <Check className="w-6 h-6 text-blue-600" />
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
              </>
            )}
            
            {/* Date First Flow */}
            {entryPoint === 'date' && (
              <div className="space-y-4">
                <h4 className="font-medium text-gray-900">Select a Date</h4>
                <div>
                  <Label>Date <span className="text-red-500">*</span></Label>
                  <div 
                    onClick={() => document.getElementById('date-first-input')?.showPicker?.()}
                    className="mt-1 cursor-pointer"
                  >
                    <Input
                      id="date-first-input"
                      type="date"
                      value={formData.date}
                      onChange={(e) => updateFormData('date', e.target.value)}
                      className="text-base cursor-pointer"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Once you pick a date, we&apos;ll show available services for that day
                  </p>
                </div>
                
                {/* Show services if date is selected */}
                {formData.date && (
                  <div className="mt-4">
                    <h4 className="font-medium text-gray-900 mb-3">Available Services on {formData.date}</h4>
                    {loadingServices ? (
                      <div className="text-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto text-blue-600" />
                      </div>
                    ) : services.length === 0 ? (
                      <div className="text-center py-8 bg-gray-50 rounded-lg">
                        <p className="text-gray-600">No services available</p>
                      </div>
                    ) : (
                      <div className="grid gap-3">
                        {services.filter(s => s.active).map((service) => (
                          <div
                            key={service.id}
                            onClick={() => {
                              setSelectedService(service);
                              updateFormData('serviceId', service.id);
                              updateFormData('serviceName', service.name);
                              updateFormData('duration', service.duration || 120);
                              updateFormData('totalAmount', service.price || 0);
                            }}
                            className={`p-4 border rounded-lg cursor-pointer transition-all ${
                              formData.serviceId === service.id
                                ? 'border-blue-600 bg-blue-50'
                                : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                            }`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <h4 className="font-semibold text-gray-900">{service.name}</h4>
                                {service.description && (
                                  <p className="text-sm text-gray-600 mt-1">{service.description}</p>
                                )}
                                <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-4 h-4" />
                                    {service.duration || 120} min
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <Users className="w-4 h-4" />
                                    Max {(() => {
                                      const isPerAsset = service?.bookingMode === 'exclusive' || service?.capacityMode === 'per_asset';
                                      const linkedAssetIds = service?.linkedAssets || service?.assetIds || [];
                                      if (isPerAsset && linkedAssetIds.length > 0 && assets?.length > 0) {
                                        const linkedAssetDetails = assets.filter(a => linkedAssetIds.includes(a.id));
                                        if (linkedAssetDetails.length > 0) {
                                          return Math.min(...linkedAssetDetails.map(a => a.capacity || 12));
                                        }
                                      }
                                      return service.capacity || 12;
                                    })()}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <CreditCard className="w-4 h-4" />
                                    {formatCurrency(service.price, service.currency)}
                                  </span>
                                </div>
                              </div>
                              {formData.serviceId === service.id && (
                                <Check className="w-6 h-6 text-blue-600 flex-shrink-0" />
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      
      case 2:
        // Date & Time - Multi-view flow like customer widget
        return (
          <div className="space-y-4">
            {/* Top navigation with back arrow */}
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => {
                  if (step2SubView === 'timeSlots') {
                    setStep2SubView('calendar');
                  } else {
                    setCurrentStep(1); // Back to services
                  }
                }}
                className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
              >
                <ChevronLeft className="w-4 h-4" />
                {step2SubView === 'calendar' && 'Back to services'}
                {step2SubView === 'timeSlots' && 'Back to calendar'}
              </button>
              
              {/* Service name + booking type indicator */}
              <div className="flex items-center gap-2">
                <div className={`flex items-center gap-1 text-xs px-2 py-1 rounded ${
                  (selectedService?.bookingMode === 'exclusive' || selectedService?.capacityMode === 'per_asset')
                    ? 'bg-purple-100 text-purple-700'
                    : 'bg-green-100 text-green-700'
                }`}>
                  {(selectedService?.bookingMode === 'exclusive' || selectedService?.capacityMode === 'per_asset') ? (
                    <>
                      <Package className="w-3 h-3" />
                      <span>Per-Asset</span>
                    </>
                  ) : (
                    <>
                      <Users className="w-3 h-3" />
                      <span>Per-Ticket</span>
                    </>
                  )}
                </div>
                <div className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded border border-blue-200">
                  {selectedService?.name}
                </div>
              </div>
            </div>

            {/* Sub-View: Calendar */}
            {step2SubView === 'calendar' && (
              <ServiceCalendarPicker
                service={selectedService}
                selectedDate={formData.date}
                selectedTime={formData.startTime}
                onDateSelect={(date) => {
                  updateFormData('date', date);
                  updateFormData('startTime', '');
                  updateFormData('endTime', '');
                  updateFormData('groupSize', 1);
                  // Advance to time slots view
                  setStep2SubView('timeSlots');
                }}
                onTimeSelect={() => {}} // Not used in calendar view
                hideTimeSlots={true}
              />
            )}

            {/* Sub-View: Time Slots with inline duration/capacity */}
            {step2SubView === 'timeSlots' && formData.date && (
              <div className="space-y-4">
                {/* Date header with navigation */}
                <div className="flex items-center justify-center gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      const newDate = addDaysToDateString(formData.date, -1);
                      updateFormData('date', newDate);
                      updateFormData('startTime', '');
                      // Don't reset groupSize - preserve user's selection
                    }}
                    className="p-2 hover:bg-gray-100 rounded-lg"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <h3 className="text-lg font-semibold">
                    {new Date(formData.date + 'T00:00:00').toLocaleDateString('en-US', { 
                      weekday: 'short', 
                      month: 'short', 
                      day: 'numeric', 
                      year: 'numeric' 
                    })}
                  </h3>
                  <button
                    type="button"
                    onClick={() => {
                      const newDate = addDaysToDateString(formData.date, 1);
                      updateFormData('date', newDate);
                      updateFormData('startTime', '');
                      // Don't reset groupSize - preserve user's selection
                    }}
                    className="p-2 hover:bg-gray-100 rounded-lg"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>

                {/* Real-Time Availability Status Indicator - Smooth transitions */}
                <div className="flex items-center justify-center gap-2 py-2 mb-3 min-h-[28px] transition-all duration-300">
                  {availabilityLoading ? (
                    <div className="flex items-center gap-2 text-blue-600 animate-pulse">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                      <span className="text-xs font-medium">Checking real-time availability...</span>
                    </div>
                  ) : availabilityReady && availabilityData ? (
                    <div className="flex items-center gap-1.5 text-green-600 animate-in fade-in duration-300">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-xs font-medium">
                        Availability checked • {availabilityData.slots?.length || 0} time slots
                      </span>
                    </div>
                  ) : availabilityReady && !availabilityData ? (
                    <div className="flex items-center gap-1.5 text-amber-600 animate-in fade-in duration-300">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <span className="text-xs font-medium">Could not check availability</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5 text-gray-400">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <circle cx="12" cy="12" r="10" strokeWidth={2} />
                      </svg>
                      <span className="text-xs font-medium">Waiting for availability data...</span>
                    </div>
                  )}
                </div>

                {/* Available times - Show loading skeleton until ready */}
                {!availabilityReady ? (
                  // Smooth loading skeleton
                  <div className="space-y-3 animate-pulse">
                    <div className="grid grid-cols-2 gap-3">
                      {[1, 2, 3, 4, 5, 6].map(i => (
                        <div key={i} className="h-14 rounded-lg bg-gray-200"></div>
                      ))}
                    </div>
                  </div>
                ) : (
                  // Actual time slots with smooth fade-in
                  <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
                {(() => {
                  const formatTime = (time24) => {
                    const [hours, minutes] = time24.split(':');
                    const hour = parseInt(hours);
                    const ampm = hour >= 12 ? 'PM' : 'AM';
                    const hour12 = hour % 12 || 12;
                    return `${hour12}:${minutes} ${ampm}`;
                  };

                  const isPerAsset = selectedService?.bookingMode === 'exclusive' || selectedService?.capacityMode === 'per_asset';
                  
                  // Normalize duration options - handle both {hours: 1} and {duration: 60}
                  let durationOptions = (selectedService?.durationOptions || []).map(opt => ({
                    hours: opt.hours || (opt.duration ? opt.duration / 60 : 1),
                    price: opt.price || selectedService?.price || 0
                  }));
                  
                  const hasDurationOptions = durationOptions.length > 0;
                  
                  // Use real-time availability data if available
                  const slots = availabilityData?.slots || [];
                  
                  // Fallback to service config if no real-time data
                  let availableTimes = [];
                  if (slots.length === 0 && !availabilityLoading) {
                    if (selectedService?.availableTimeSlots) {
                      const date = new Date(formData.date + 'T00:00:00');
                      const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
                      const dayName = dayNames[date.getDay()];
                      
                      selectedService.availableTimeSlots.forEach(slot => {
                        if (slot.days && slot.days.map(d => d.toLowerCase()).includes(dayName)) {
                          availableTimes.push({ time: slot.time, available: true, durations: [] });
                        }
                      });
                      availableTimes.sort((a, b) => a.time.localeCompare(b.time));
                    }
                  } else {
                    availableTimes = slots;
                  }

                  return availableTimes.length > 0 ? (
                    <div className="space-y-3">
                      {/* Time slot buttons with real-time availability */}
                      <div className="grid grid-cols-2 gap-3">
                        {availableTimes.map(slotData => {
                          const time = slotData.time || slotData;
                          const isGreyedOut = slotData.greyedOut;
                          const reason = slotData.reason;
                          const slotDurations = slotData.durations || [];
                          
                          // Check if ANY duration has availability
                          const hasAnyAvailability = slotDurations.length === 0 || slotDurations.some(d => d.available > 0);
                          const isSoldOut = slotDurations.length > 0 && slotDurations.every(d => d.available === 0);
                          
                          return (
                            <button
                              key={time}
                              type="button"
                              disabled={isGreyedOut || isSoldOut}
                              onClick={() => {
                                if (isGreyedOut || isSoldOut) return;
                                
                                if (formData.startTime === time) {
                                  // Deselect if clicking same time - reset everything
                                  updateFormData('startTime', '');
                                  updateFormData('endTime', '');
                                  updateFormData('groupSize', 1);
                                } else {
                                  updateFormData('startTime', time);
                                  // Auto-calculate end time
                                  if (selectedService?.duration) {
                                    const [hours, minutes] = time.split(':').map(Number);
                                    const totalMinutes = hours * 60 + minutes + selectedService.duration;
                                    const endHours = Math.floor(totalMinutes / 60) % 24;
                                    const endMinutes = totalMinutes % 60;
                                    const endTime = `${String(endHours).padStart(2, '0')}:${String(endMinutes).padStart(2, '0')}`;
                                    updateFormData('endTime', endTime);
                                  }
                                }
                              }}
                              className={`
                                py-4 rounded-lg text-sm font-semibold transition-all relative
                                ${isGreyedOut || isSoldOut
                                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                  : formData.startTime === time 
                                    ? 'bg-blue-800 text-white' 
                                    : 'bg-blue-600 text-white hover:bg-blue-700'}
                              `}
                            >
                              <span>{slotData.displayTime || formatTime(time)} {!slotData.displayTime && getTimezoneAbbreviation(selectedService?.timezone || orgTimezone)}</span>
                              {isSoldOut && (
                                <span className="absolute top-1 right-1 text-[10px] bg-red-500 text-white px-1.5 py-0.5 rounded">
                                  SOLD OUT
                                </span>
                              )}
                              {reason === 'minimum_notice' && (
                                <span className="absolute top-1 right-1 text-[10px] bg-gray-500 text-white px-1.5 py-0.5 rounded">
                                  TOO LATE
                                </span>
                              )}
                              {/* Show available count for per-asset */}
                              {hasAnyAvailability && slotDurations.length > 0 && !isGreyedOut && !isSoldOut && (
                                <span className="block text-[10px] mt-1 opacity-80">
                                  {isPerAsset 
                                    ? `${slotDurations[0]?.available || 0} ${slotDurations[0]?.available === 1 ? 'boat' : 'boats'} left`
                                    : `${slotDurations[0]?.available || 0} spots left`
                                  }
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>

                      {/* Duration/Capacity appears below selected time */}
                      {formData.startTime && (
                        <div className="pt-3 animate-in slide-in-from-top-2 duration-300">
                          {(() => {
                            // Get real-time availability for selected slot
                            const selectedSlot = availableTimes.find(s => (s.time || s) === formData.startTime);
                            const slotDurations = selectedSlot?.durations || [];
                            
                            // Use real-time data if available, otherwise calculate from service
                            const linkedAssetIds = selectedService?.linkedAssets || selectedService?.assetIds || [];
                            const numAssets = linkedAssetIds.length || 1;
                            let assetCapacity = selectedService?.capacity || 12;
                            
                            if (isPerAsset && linkedAssetIds.length > 0 && assets && assets.length > 0) {
                              const linkedAssetDetails = assets.filter(a => linkedAssetIds.includes(a.id));
                              if (linkedAssetDetails.length > 0) {
                                assetCapacity = Math.min(...linkedAssetDetails.map(a => a.capacity || 12));
                              }
                            }
                            
                            return isPerAsset ? (
                            // Per-Asset: Show booking options with REAL-TIME availability
                            <div className="space-y-2">
                              {hasDurationOptions ? (
                                durationOptions.map((duration, idx) => {
                                  // Get real-time availability for this duration
                                  const realTimeData = slotDurations.find(d => Math.abs(d.hours - duration.hours) < 0.1);
                                  const availableCount = realTimeData?.available ?? numAssets;
                                  const selectedCount = formData.selectedDurationIndex === idx ? (formData.groupSize || 0) : 0;
                                  const remaining = availableCount - selectedCount;
                                  const isSoldOut = availableCount === 0;
                                  
                                  return (
                                    <div
                                      key={idx}
                                      className={`
                                        p-3 border rounded-lg transition-all
                                        ${isSoldOut ? 'border-gray-200 bg-gray-50 opacity-60' : 
                                          formData.selectedDurationIndex === idx ? 'border-blue-600 bg-blue-50' : 'border-gray-200'}
                                      `}
                                    >
                                      <div className="flex items-center justify-between">
                                        <div>
                                          <p className={`font-semibold ${isSoldOut ? 'text-gray-400' : 'text-gray-900'}`}>
                                            {duration.hours} {duration.hours === 1 ? 'Hour' : 'Hours'} - {formatCurrency(realTimeData?.price || duration.price, selectedService.currency)}
                                          </p>
                                          <p className={`text-xs ${isSoldOut ? 'text-red-500 font-medium' : 'text-gray-500'}`}>
                                            {isSoldOut ? '❌ SOLD OUT' : `${remaining} available (up to ${assetCapacity} guests per booking)`}
                                          </p>
                                        </div>
                                        {!isSoldOut && (
                                          <div className="flex items-center gap-2">
                                            <button
                                              type="button"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                if (formData.selectedDurationIndex !== idx) {
                                                  updateFormData('selectedDurationIndex', idx);
                                                  updateFormData('duration', duration.hours * 60);
                                                  updateFormData('groupSize', 1);
                                                } else {
                                                  updateFormData('groupSize', Math.max(1, (formData.groupSize || 0) - 1));
                                                }
                                              }}
                                              className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 font-bold text-lg"
                                            >
                                              −
                                            </button>
                                            <span className="w-10 text-center font-bold text-lg">{selectedCount}</span>
                                            <button
                                              type="button"
                                              disabled={selectedCount >= availableCount}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                updateFormData('selectedDurationIndex', idx);
                                                updateFormData('duration', duration.hours * 60);
                                                updateFormData('groupSize', Math.min(availableCount, (selectedCount || 0) + 1));
                                              }}
                                              className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg
                                                ${selectedCount >= availableCount 
                                                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                                                  : 'bg-blue-600 text-white hover:bg-blue-700'}`}
                                            >
                                              +
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })
                              ) : (
                                // Single duration
                                <div className="p-3 border border-gray-200 rounded-lg">
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <p className="font-semibold text-gray-900">{selectedService.duration || 60} min</p>
                                      <p className="text-xs text-gray-500">
                                        {numAssets - (formData.groupSize || 0)} available (up to {assetCapacity} guests per booking)
                                      </p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <button
                                        type="button"
                                        onClick={() => updateFormData('groupSize', Math.max(1, (formData.groupSize || 1) - 1))}
                                        className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 font-bold text-lg"
                                      >
                                        −
                                      </button>
                                      <span className="w-10 text-center font-bold text-lg">{formData.groupSize || 0}</span>
                                      <button
                                        type="button"
                                        onClick={() => updateFormData('groupSize', Math.min(numAssets, (formData.groupSize || 0) + 1))}
                                        className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 font-bold text-lg"
                                      >
                                        +
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : (
                            // Per-Ticket: Show ticket options with REAL-TIME availability
                            <div className="space-y-2">
                              {hasDurationOptions ? (
                                durationOptions.map((duration, idx) => {
                                  // Get real-time availability for this duration
                                  const realTimeData = slotDurations.find(d => Math.abs(d.hours - duration.hours) < 0.1);
                                  const totalCapacity = realTimeData?.available ?? (selectedService?.capacity || 12);
                                  const selectedCount = formData[`tickets_duration_${idx}`] || 0;
                                  const remaining = totalCapacity - selectedCount;
                                  const isSoldOut = totalCapacity === 0;
                                  
                                  return (
                                    <div
                                      key={idx}
                                      className={`
                                        p-3 border rounded-lg transition-all
                                        ${isSoldOut ? 'border-gray-200 bg-gray-50 opacity-60' :
                                          selectedCount > 0 ? 'border-blue-600 bg-blue-50' : 'border-gray-200'}
                                      `}
                                    >
                                      <div className="flex items-center justify-between">
                                        <div>
                                          <p className={`font-semibold ${isSoldOut ? 'text-gray-400' : 'text-gray-900'}`}>
                                            {duration.hours} {duration.hours === 1 ? 'Hour' : 'Hours'} - {formatCurrency(realTimeData?.price || duration.price, selectedService.currency)}
                                          </p>
                                          <p className={`text-xs ${isSoldOut ? 'text-red-500 font-medium' : 'text-gray-500'}`}>
                                            {isSoldOut ? '❌ SOLD OUT' : `${remaining} tickets available`}
                                          </p>
                                        </div>
                                        {!isSoldOut && (
                                          <div className="flex items-center gap-2">
                                            <button
                                              type="button"
                                              onClick={() => {
                                                const current = formData[`tickets_duration_${idx}`] || 0;
                                                const newValue = Math.max(0, current - 1);
                                                
                                                // Calculate new total by summing all duration ticket counts
                                                let newTotal = 0;
                                                durationOptions.forEach((_, i) => {
                                                  if (i === idx) {
                                                    newTotal += newValue;
                                                  } else {
                                                    newTotal += formData[`tickets_duration_${i}`] || 0;
                                                  }
                                                });
                                                
                                                // Update both values
                                                setFormData(prev => ({
                                                  ...prev,
                                                  [`tickets_duration_${idx}`]: newValue,
                                                  groupSize: Math.max(1, newTotal), // Ensure at least 1 for validation
                                                  selectedDurationIndex: newTotal > 0 ? idx : prev.selectedDurationIndex,
                                                  duration: duration.hours * 60
                                                }));
                                              }}
                                              className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 font-bold text-lg"
                                            >
                                              −
                                            </button>
                                            <span className="w-10 text-center font-bold text-lg">{selectedCount}</span>
                                            <button
                                              type="button"
                                              disabled={selectedCount >= totalCapacity}
                                              onClick={() => {
                                                const current = formData[`tickets_duration_${idx}`] || 0;
                                                const newValue = Math.min(totalCapacity, current + 1);
                                                
                                                // Calculate new total by summing all duration ticket counts
                                                let newTotal = 0;
                                                durationOptions.forEach((_, i) => {
                                                  if (i === idx) {
                                                    newTotal += newValue;
                                                  } else {
                                                    newTotal += formData[`tickets_duration_${i}`] || 0;
                                                  }
                                                });
                                                
                                                // Update all values in a single state update
                                                setFormData(prev => ({
                                                  ...prev,
                                                  [`tickets_duration_${idx}`]: newValue,
                                                  groupSize: newTotal,
                                                  selectedDurationIndex: idx,
                                                  duration: duration.hours * 60
                                              }));
                                            }}
                                            className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg
                                              ${selectedCount >= totalCapacity 
                                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                                                : 'bg-blue-600 text-white hover:bg-blue-700'}`}
                                          >
                                            +
                                          </button>
                                        </div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })
                              ) : (
                                // Single duration with real-time availability
                                (() => {
                                  const realTimeData = slotDurations[0];
                                  const totalCapacity = realTimeData?.available ?? (selectedService?.capacity || 12);
                                  const isSoldOut = totalCapacity === 0;
                                  
                                  return (
                                    <div className={`p-3 border rounded-lg ${isSoldOut ? 'border-gray-200 bg-gray-50 opacity-60' : 'border-gray-200'}`}>
                                      <div className="flex items-center justify-between">
                                        <div>
                                          <p className={`font-semibold ${isSoldOut ? 'text-gray-400' : 'text-gray-900'}`}>
                                            {selectedService.duration || 60} min
                                          </p>
                                          <p className={`text-xs ${isSoldOut ? 'text-red-500 font-medium' : 'text-gray-500'}`}>
                                            {isSoldOut ? '❌ SOLD OUT' : `${totalCapacity - (formData.groupSize || 0)} tickets available`}
                                          </p>
                                        </div>
                                        {!isSoldOut && (
                                          <div className="flex items-center gap-2">
                                            <button
                                              type="button"
                                              onClick={() => updateFormData('groupSize', Math.max(1, (formData.groupSize || 1) - 1))}
                                              className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 font-bold text-lg"
                                            >
                                              −
                                            </button>
                                            <span className="w-10 text-center font-bold text-lg">{formData.groupSize || 0}</span>
                                            <button
                                              type="button"
                                              disabled={(formData.groupSize || 0) >= totalCapacity}
                                              onClick={() => updateFormData('groupSize', Math.min(totalCapacity, (formData.groupSize || 0) + 1))}
                                              className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg
                                                ${(formData.groupSize || 0) >= totalCapacity 
                                                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                                                  : 'bg-blue-600 text-white hover:bg-blue-700'}`}
                                            >
                                              +
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })()
                              )}
                            </div>
                          );
                          })()}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-12 bg-gray-50 rounded-lg">
                      <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p className="text-sm text-gray-500">No time slots for this date</p>
                    </div>
                  );
                })()}
                  </div>
                )}
              </div>
            )}
          </div>
        );

      case 3:
        // Add-ons (optional, skip if no add-ons) - supports both regular and custom bookings
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Package className="w-5 h-5" />
              {entryPoint === 'custom' ? 'Add-ons & Extras' : 'Enhance Your Booking'}
            </h3>

            {/* Custom Booking Add-ons */}
            {entryPoint === 'custom' ? (
              <div className="space-y-4">
                {/* Existing org add-ons */}
                {allOrgAddOns.length > 0 && (
                  <div className="space-y-3">
                    <p className="text-sm font-medium text-gray-700">Select from existing add-ons:</p>
                    {allOrgAddOns.map((addon, index) => {
                      const addonKey = addon.id || `org_addon_${index}`;
                      const isSelected = formData.selectedAddOns[addonKey] !== undefined;

                      return (
                        <div
                          key={addonKey}
                          onClick={() => {
                            const newAddOns = { ...formData.selectedAddOns };
                            if (isSelected) {
                              delete newAddOns[addonKey];
                            } else {
                              newAddOns[addonKey] = {
                                id: addonKey,
                                title: addon.title,
                                description: addon.description || '',
                                price: addon.price || 0,
                                type: 'checkbox'
                              };
                            }
                            updateFormData('selectedAddOns', newAddOns);
                          }}
                          className={`
                            p-3 border rounded-lg cursor-pointer transition-all
                            ${isSelected ? 'border-purple-600 bg-purple-50' : 'border-gray-300 hover:border-purple-400'}
                          `}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-3 flex-1">
                              <div className={`
                                w-5 h-5 rounded border flex items-center justify-center flex-shrink-0
                                ${isSelected ? 'bg-purple-600 border-purple-600' : 'border-gray-300'}
                              `}>
                                {isSelected && <Check className="w-3 h-3 text-white" />}
                              </div>
                              <div className="flex-1">
                                <p className="font-medium text-gray-900 text-sm">{addon.title}</p>
                                {addon.description && <p className="text-xs text-gray-500">{addon.description}</p>}
                              </div>
                            </div>
                            <p className="font-semibold text-purple-600 text-sm">
                              {addon.price > 0 ? `+${formatCurrency(addon.price)}` : 'Free'}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Custom Add-ons Section */}
                <div className="border-t pt-4 mt-4">
                  <p className="text-sm font-medium text-gray-700 mb-3">Custom add-ons:</p>
                  
                  {/* List of custom add-ons */}
                  {customBookingData.customAddOns.map((addon, index) => (
                    <div key={index} className="flex items-center gap-2 mb-2 p-2 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <p className="text-sm font-medium">{addon.description}</p>
                      </div>
                      <p className="text-sm font-semibold text-purple-600">{formatCurrency(addon.amount)}</p>
                      <button
                        onClick={() => {
                          const newCustomAddOns = customBookingData.customAddOns.filter((_, i) => i !== index);
                          setCustomBookingData({...customBookingData, customAddOns: newCustomAddOns});
                        }}
                        className="p-1 text-red-500 hover:bg-red-50 rounded"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}

                  {/* Add custom add-on form */}
                  <div className="flex gap-2 mt-3">
                    <Input
                      placeholder="Description (e.g., BBQ Tray)"
                      className="flex-1 text-sm"
                      id="customAddonDesc"
                    />
                    <div className="relative w-24">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                        className="pl-5 text-sm"
                        id="customAddonAmount"
                      />
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => {
                        const descInput = document.getElementById('customAddonDesc');
                        const amountInput = document.getElementById('customAddonAmount');
                        const desc = descInput?.value?.trim();
                        const amount = parseFloat(amountInput?.value) || 0;
                        
                        if (desc && amount > 0) {
                          setCustomBookingData({
                            ...customBookingData,
                            customAddOns: [...customBookingData.customAddOns, { description: desc, amount }]
                          });
                          descInput.value = '';
                          amountInput.value = '';
                        } else {
                          toast.error('Please enter description and amount');
                        }
                      }}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      Add
                    </Button>
                  </div>
                </div>

                {/* Total Preview */}
                <div className="bg-purple-50 rounded-lg p-3 mt-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Base Price:</span>
                    <span className="font-medium">{formatCurrency(parseFloat(customBookingData.basePrice) || 0)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Add-ons:</span>
                    <span className="font-medium">
                      {formatCurrency(
                        Object.values(formData.selectedAddOns).reduce((sum, a) => sum + (a.price || 0), 0) +
                        customBookingData.customAddOns.reduce((sum, a) => sum + (a.amount || 0), 0)
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between font-semibold text-purple-700 mt-2 pt-2 border-t border-purple-200">
                    <span>Subtotal:</span>
                    <span>
                      {formatCurrency(
                        (parseFloat(customBookingData.basePrice) || 0) +
                        Object.values(formData.selectedAddOns).reduce((sum, a) => sum + (a.price || 0), 0) +
                        customBookingData.customAddOns.reduce((sum, a) => sum + (a.amount || 0), 0)
                      )}
                    </span>
                  </div>
                </div>
              </div>
            ) : selectedService?.addOns && selectedService.addOns.length > 0 ? (
              <div className="space-y-3">
                {selectedService.addOns.map((addon, index) => {
                  const addonKey = addon.id || `addon_${index}`;
                  const isSelected = formData.selectedAddOns[addonKey] !== undefined;
                  const addonPrice = addon.type === 'percentage'
                    ? (selectedService.price * (addon.percentage || 0) / 100)
                    : (addon.price || 0);

                  return (
                    <div
                      key={addonKey}
                      onClick={() => {
                        const newAddOns = { ...formData.selectedAddOns };
                        if (isSelected) {
                          delete newAddOns[addonKey];
                        } else {
                          // Store full add-on details including title for display on tickets
                          newAddOns[addonKey] = {
                            id: addonKey,
                            title: addon.title || addon.name || `Add-on ${index + 1}`,
                            description: addon.description || '',
                            price: addonPrice,
                            type: addon.type || 'checkbox'
                          };
                        }
                        updateFormData('selectedAddOns', newAddOns);
                        
                        // Update total amount - now accessing .price from object
                        const addOnsTotal = Object.values(newAddOns).reduce((sum, a) => sum + (a.price || 0), 0);
                        updateFormData('totalAmount', (selectedService.price || 0) + addOnsTotal);
                      }}
                      className={`
                        p-4 border rounded-lg cursor-pointer transition-all
                        ${isSelected ? 'border-blue-600 bg-blue-50' : 'border-gray-300 hover:border-blue-400'}
                      `}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 flex-1">
                          <div className={`
                            w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 mt-0.5
                            ${isSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300'}
                          `}>
                            {isSelected && <Check className="w-3 h-3 text-white" />}
                          </div>
                          <div className="flex-1">
                            <p className="font-semibold text-gray-900">
                              {addon.title || addon.name}
                              {addon.required && <span className="text-red-500 ml-1">*</span>}
                            </p>
                            <ExpandableAddOnDescription description={addon.description} maxLength={80} />
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="font-semibold text-blue-600">
                            {addonPrice > 0 ? `+${formatCurrency(addonPrice, selectedService?.currency)}` : 'Free'}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="text-sm text-gray-500">No add-ons available for this service</p>
              </div>
            )}
          </div>
        );

      case 4:
        // Customer Details - Simplified
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <User className="w-5 h-5" />
              Customer Details
            </h3>

            <div>
              <Label>Customer Name <span className="text-red-500">*</span></Label>
              <Input
                value={formData.customerName}
                onChange={(e) => updateFormData('customerName', e.target.value)}
                placeholder="John Doe"
                className="mt-1"
                required
              />
            </div>

            <div>
              <Label>Customer Email <span className="text-red-500">*</span></Label>
              <Input
                type="email"
                value={formData.customerEmail}
                onChange={(e) => updateFormData('customerEmail', e.target.value)}
                placeholder="email@example.com"
                className="mt-1"
                required
              />
            </div>

            <div>
              <Label>Customer Phone <span className="text-red-500">*</span></Label>
              <Input
                type="tel"
                value={formData.customerPhone}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, '');
                  updateFormData('customerPhone', value);
                }}
                placeholder="5551234567"
                className="mt-1"
                maxLength={15}
                required
              />
              <p className="text-xs text-gray-400 mt-0.5">Numbers only</p>
            </div>

            <div>
              <Label>Pickup Location</Label>
              <Input
                value={formData.pickupLocation}
                onChange={(e) => updateFormData('pickupLocation', e.target.value)}
                placeholder="Enter pickup location"
                className="mt-1"
              />
              <div className="flex items-center justify-between mt-2">
                <button
                  type="button"
                  onClick={() => {
                    const defaultLocation = localStorage.getItem('defaultPickupLocation') || '';
                    if (defaultLocation) {
                      updateFormData('pickupLocation', defaultLocation);
                      toast.success('Default location applied');
                    } else {
                      toast.error('No default location saved yet');
                    }
                  }}
                  className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                >
                  Use default location
                </button>
                {formData.pickupLocation && (
                  <button
                    type="button"
                    onClick={() => {
                      localStorage.setItem('defaultPickupLocation', formData.pickupLocation);
                      toast.success('Saved as default location');
                    }}
                    className="text-xs text-green-600 hover:text-green-700 font-medium"
                  >
                    Save as default
                  </button>
                )}
              </div>
              <p className="text-xs text-gray-400 mt-1">
                Don&apos;t worry - you can always change this on individual bookings later
              </p>
            </div>
          </div>
        );

      case 5:
        // Review & Confirm - handles both regular and custom bookings
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Check className="w-5 h-5" />
              Review & Confirm
            </h3>

            {/* Pricing Breakdown */}
            {(() => {
              // Handle custom booking pricing
              const isCustom = entryPoint === 'custom';
              const basePrice = isCustom 
                ? parseFloat(customBookingData.basePrice) || 0
                : parseFloat(selectedService?.price || 0);
              
              // Regular add-ons
              const regularAddOnsTotal = Object.values(formData.selectedAddOns).reduce((sum, addon) => sum + (addon?.price || 0), 0);
              // Custom add-ons (only for custom bookings)
              const customAddOnsTotal = isCustom 
                ? customBookingData.customAddOns.reduce((sum, addon) => sum + (addon?.amount || 0), 0)
                : 0;
              const addOnsTotal = regularAddOnsTotal + customAddOnsTotal;
              
              const preTotal = basePrice + addOnsTotal;
              const platformFee = (preTotal * 0.059) + 0.30;
              
              // Get tax settings - for custom bookings, use org defaults
              const taxEnabled = isCustom 
                ? true // Custom bookings always show tax
                : (selectedService?.taxEnabled || selectedService?.salesTaxEnabled || false);
              let taxRateRaw = isCustom 
                ? 8.25 // Default tax rate for custom - TODO: get from org settings
                : (selectedService?.taxRate || selectedService?.salesTaxRate || 0);
              const taxRateDecimal = taxRateRaw > 1 ? taxRateRaw / 100 : taxRateRaw;
              const salesTax = taxEnabled ? preTotal * taxRateDecimal : 0;
              const grandTotal = preTotal + platformFee + salesTax;

              const currency = isCustom ? 'USD' : (selectedService?.currency || 'USD');

              return (
                <div className={`${isCustom ? 'bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200' : 'bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200'} border rounded-lg p-4 space-y-2`}>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-700">{isCustom ? 'Custom Price:' : 'Service Price:'}</span>
                    <span className="font-semibold">{formatCurrency(basePrice, currency)}</span>
                  </div>
                  
                  {addOnsTotal > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-700">Add-ons:</span>
                      <span className="font-semibold">+{formatCurrency(addOnsTotal, currency)}</span>
                    </div>
                  )}

                  <div className="border-t border-gray-200 pt-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-700">Subtotal:</span>
                      <span className="font-semibold">{formatCurrency(preTotal, currency)}</span>
                    </div>
                  </div>

                  {taxEnabled && salesTax > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-700">Tax ({taxRateRaw > 1 ? taxRateRaw.toFixed(2) : (taxRateRaw * 100).toFixed(2)}%):</span>
                      <span className="font-semibold">+{formatCurrency(salesTax, currency)}</span>
                    </div>
                  )}

                  <div className="flex justify-between text-sm">
                    <span className="text-gray-700">Platform Fee:</span>
                    <span className="font-semibold">+{formatCurrency(platformFee, currency)}</span>
                  </div>

                  <div className={`border-t ${isCustom ? 'border-purple-300' : 'border-blue-300'} pt-2`}>
                    <div className={`flex justify-between text-lg font-bold ${isCustom ? 'text-purple-700' : 'text-blue-700'}`}>
                      <span>Total Amount:</span>
                      <span>{formatCurrency(grandTotal, currency)}</span>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Booking Summary */}
            <div className="space-y-3 text-sm">
              <div className="border-b pb-2">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                  {entryPoint === 'custom' ? 'Custom Service' : 'Service'}
                </p>
                <p className="font-semibold text-gray-900">
                  {entryPoint === 'custom' ? customBookingData.serviceName : selectedService?.name}
                </p>
                {entryPoint === 'custom' && customBookingData.notes && (
                  <p className="text-xs text-gray-600 mt-1">{customBookingData.notes}</p>
                )}
              </div>

              <div className="border-b pb-2">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Date & Time</p>
                <p className="font-semibold text-gray-900">
                  {new Date(formData.date + 'T00:00:00').toLocaleDateString('en-US', { 
                    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' 
                  })} at {formData.startTime}
                </p>
              </div>

              <div className="border-b pb-2">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Customer</p>
                <p className="font-semibold text-gray-900">{formData.customerName}</p>
                <p className="text-xs text-gray-600">{formData.customerEmail}</p>
                <p className="text-xs text-gray-600">{formData.customerPhone}</p>
              </div>

              {formData.pickupLocation && (
                <div className="border-b pb-2">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Location</p>
                  <p className="font-semibold text-gray-900">
                    {formData.pickupLocation}
                  </p>
                </div>
              )}

              {/* Show custom add-ons if any */}
              {entryPoint === 'custom' && customBookingData.customAddOns.length > 0 && (
                <div className="border-b pb-2">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Custom Add-ons</p>
                  {customBookingData.customAddOns.map((addon, idx) => (
                    <div key={idx} className="flex justify-between text-xs">
                      <span>{addon.description}</span>
                      <span className="font-medium">{formatCurrency(addon.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );

      case 6:
        // Payment
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Payment
            </h3>

            {/* Payment Method Selector - 4 Clean Options */}
            <div className="grid gap-3">
              {/* Option 1: Pay Now (Redirect to Stripe) */}
              <div
                onClick={() => updateFormData('paymentStatus', 'pay_now_redirect')}
                className={`
                  p-4 border rounded-lg cursor-pointer transition-all
                  ${formData.paymentStatus === 'pay_now_redirect' ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}
                `}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`
                      w-10 h-10 rounded-full flex items-center justify-center
                      ${formData.paymentStatus === 'pay_now_redirect' ? 'bg-blue-600' : 'bg-gray-100'}
                    `}>
                      <CreditCard className={`w-5 h-5 ${formData.paymentStatus === 'pay_now_redirect' ? 'text-white' : 'text-gray-600'}`} />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Pay Now</p>
                      <p className="text-xs text-gray-600">Enter card details via Stripe (over phone)</p>
                    </div>
                  </div>
                  {formData.paymentStatus === 'pay_now_redirect' && <Check className="w-5 h-5 text-blue-600" />}
                </div>
              </div>

              {/* Option 2: Share Payment Link */}
              <div
                onClick={() => updateFormData('paymentStatus', 'send_link')}
                className={`
                  p-4 border rounded-lg cursor-pointer transition-all
                  ${formData.paymentStatus === 'send_link' ? 'border-purple-600 bg-purple-50' : 'border-gray-200 hover:border-purple-300'}
                `}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`
                      w-10 h-10 rounded-full flex items-center justify-center
                      ${formData.paymentStatus === 'send_link' ? 'bg-purple-600' : 'bg-gray-100'}
                    `}>
                      <Link2 className={`w-5 h-5 ${formData.paymentStatus === 'send_link' ? 'text-white' : 'text-gray-600'}`} />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Share Payment Link</p>
                      <p className="text-xs text-gray-600">Send Stripe link to customer via SMS/Email</p>
                    </div>
                  </div>
                  {formData.paymentStatus === 'send_link' && <Check className="w-5 h-5 text-purple-600" />}
                </div>
              </div>

              {/* Option 3: Already Paid */}
              <div
                onClick={() => updateFormData('paymentStatus', 'paid')}
                className={`
                  p-4 border rounded-lg cursor-pointer transition-all
                  ${formData.paymentStatus === 'paid' ? 'border-green-600 bg-green-50' : 'border-gray-200 hover:border-green-300'}
                `}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`
                      w-10 h-10 rounded-full flex items-center justify-center
                      ${formData.paymentStatus === 'paid' ? 'bg-green-600' : 'bg-gray-100'}
                    `}>
                      <Check className={`w-5 h-5 ${formData.paymentStatus === 'paid' ? 'text-white' : 'text-gray-600'}`} />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Already Paid</p>
                      <p className="text-xs text-gray-600">Payment collected externally (cash, check, etc.)</p>
                    </div>
                  </div>
                  {formData.paymentStatus === 'paid' && <Check className="w-5 h-5 text-green-600" />}
                </div>
              </div>

              {/* Option 4: Will Pay Later */}
              <div
                onClick={() => updateFormData('paymentStatus', 'unpaid')}
                className={`
                  p-4 border rounded-lg cursor-pointer transition-all
                  ${formData.paymentStatus === 'unpaid' ? 'border-yellow-600 bg-yellow-50' : 'border-gray-200 hover:border-yellow-300'}
                `}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`
                      w-10 h-10 rounded-full flex items-center justify-center
                      ${formData.paymentStatus === 'unpaid' ? 'bg-yellow-600' : 'bg-gray-100'}
                    `}>
                      <Clock className={`w-5 h-5 ${formData.paymentStatus === 'unpaid' ? 'text-white' : 'text-gray-600'}`} />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Will Pay Later</p>
                      <p className="text-xs text-gray-600">Deposit, on arrival, invoice, etc.</p>
                    </div>
                  </div>
                  {formData.paymentStatus === 'unpaid' && <Check className="w-5 h-5 text-yellow-600" />}
                </div>
              </div>
            </div>

            {/* Multi-Currency Selector - Only for Stripe payment options */}
            {(formData.paymentStatus === 'pay_now_redirect' || formData.paymentStatus === 'send_link') && supportedCurrencies.length > 0 && (
              <div className="mt-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-lg">💱</span>
                  <h4 className="font-semibold text-gray-800">Customer&apos;s Currency</h4>
                </div>
                <p className="text-sm text-gray-600 mb-3">
                  Charge in a different currency for international customers
                </p>
                
                <select
                  value={formData.presentmentCurrency || (selectedService?.currency || 'USD').toLowerCase()}
                  onChange={(e) => updateFormData('presentmentCurrency', e.target.value)}
                  className="w-full p-3 border rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {/* Default - Service Currency (No conversion) */}
                  <option value={(selectedService?.currency || 'USD').toLowerCase()}>
                    {supportedCurrencies.find(c => c.code === (selectedService?.currency || 'USD').toUpperCase())?.flag || '🏳️'} {(selectedService?.currency || 'USD').toUpperCase()} - Default (No conversion)
                  </option>
                  
                  {/* All currencies alphabetically by ISO code */}
                  {supportedCurrencies
                    .filter(c => c.code.toLowerCase() !== (selectedService?.currency || 'USD').toLowerCase())
                    .sort((a, b) => a.code.localeCompare(b.code))
                    .map(currency => (
                      <option key={currency.code} value={currency.code.toLowerCase()}>
                        {currency.flag} {currency.code} - {currency.name}
                      </option>
                    ))
                  }
                </select>
                
                {/* Converted Price Display */}
                {loadingConversion && (
                  <div className="mt-3 text-sm text-gray-500 flex items-center gap-2">
                    <div className="animate-spin w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full" />
                    Calculating conversion...
                  </div>
                )}
                
                {convertedPrice && !loadingConversion && (
                  <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Original Total:</span>
                      <span className="font-medium">
                        {convertedPrice.fromCurrency} {convertedPrice.originalAmount.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center mt-1">
                      <span className="text-sm text-gray-600">Rate (incl. 2% fee):</span>
                      <span className="text-sm">
                        1 {convertedPrice.fromCurrency} = {convertedPrice.rateWithFee.toFixed(4)} {convertedPrice.toCurrency}
                      </span>
                    </div>
                    <div className="flex justify-between items-center mt-2 pt-2 border-t border-blue-200">
                      <span className="font-semibold text-gray-800">Customer Pays:</span>
                      <span className="text-lg font-bold text-blue-600">
                        {supportedCurrencies.find(c => c.code === convertedPrice.toCurrency)?.symbol || convertedPrice.toCurrency} {convertedPrice.convertedAmount.toFixed(2)}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      💡 Stripe converts to {(selectedService?.currency || 'USD').toUpperCase()} when settling to your account
                    </p>
                  </div>
                )}
                
                {!formData.presentmentCurrency || formData.presentmentCurrency === (selectedService?.currency || 'USD').toLowerCase() ? (
                  <p className="text-xs text-gray-500 mt-2">
                    Customer will be charged in {(selectedService?.currency || 'USD').toUpperCase()} (your default currency)
                  </p>
                ) : null}
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          margin: 0,
          padding: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(4px)',
          zIndex: 50,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onCancel();
          }
        }}
      >
        <div 
          className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col"
          style={{ margin: '16px' }}
          onClick={(e) => e.stopPropagation()}
        >
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-gray-900">Add Booking</h2>
            <button onClick={onCancel} className="text-gray-400 hover:text-gray-600 transition">
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Continuous Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
            <div 
              className="bg-blue-600 h-full rounded-full transition-all duration-300 ease-out"
              style={{ width: `${(getStep2Progress() / totalSteps) * 100}%` }}
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {renderStepContent()}
        </div>

        {/* Footer Buttons */}
        <div className="p-6 border-t flex items-center justify-between">
          <Button
            variant="outline"
            onClick={prevStep}
            disabled={currentStep === 1}
            className="flex items-center gap-2"
          >
            <ChevronLeft className="w-4 h-4" />
            Back
          </Button>

          {currentStep === totalSteps ? (
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting || !formData.paymentStatus}
              className="bg-green-600 hover:bg-green-700 flex items-center gap-2 text-white font-semibold disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Creating Booking...
                </>
              ) : (
                <>
                  Confirm & Create Booking
                  <Check className="w-4 h-4" />
                </>
              )}
            </Button>
          ) : (
            <Button
              onClick={nextStep}
              className="bg-blue-600 hover:bg-blue-700 flex items-center gap-2"
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Success Modal */}
      {showSuccessModal && createdBooking && (
        <>
          {/* Confetti Animation */}
          <div className="fixed inset-0 z-[80] pointer-events-none overflow-hidden">
            {[...Array(50)].map((_, i) => (
              <div
                key={i}
                className="absolute w-2.5 h-2.5 animate-bounce"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: '-10px',
                  backgroundColor: ['#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'][i % 6],
                  borderRadius: Math.random() > 0.5 ? '50%' : '2px',
                  animation: `confetti-fall ${2 + Math.random() * 2}s ease-out ${Math.random() * 0.5}s forwards`,
                }}
              />
            ))}
          </div>
          <style>{`
            @keyframes confetti-fall {
              0% { transform: translateY(0) rotate(0deg); opacity: 1; }
              100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
            }
          `}</style>
          
          {/* Success modal content */}
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              margin: 0,
              padding: '16px',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(4px)',
              zIndex: 70,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onClick={async () => {
              setShowSuccessModal(false);
              onCancel();
              if (window.refreshBookingData) {
                await window.refreshBookingData();
              }
            }}
          >
          <div 
            className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto border border-gray-100"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Success Header */}
            <div className={`p-8 text-center ${
              createdBooking.paymentStatus === 'paid' 
                ? 'bg-gradient-to-br from-emerald-500 to-green-600' 
                : createdBooking.paymentStatus === 'pending'
                ? 'bg-gradient-to-br from-blue-500 to-indigo-600'
                : 'bg-gradient-to-br from-amber-500 to-orange-600'
            }`}>
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Check className={`w-12 h-12 ${
                  createdBooking.paymentStatus === 'paid' ? 'text-emerald-600' :
                  createdBooking.paymentStatus === 'pending' ? 'text-blue-600' :
                  'text-amber-600'
                }`} strokeWidth={2.5} />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">
                {createdBooking.paymentStatus === 'paid' ? 'Booking Confirmed!' :
                 createdBooking.paymentStatus === 'pending' ? 'Payment Link Sent!' :
                 'Booking Created!'}
              </h2>
              <p className="text-white/90 text-sm">
                {createdBooking.paymentStatus === 'paid' ? 'Payment received • Customer notified' :
                 createdBooking.paymentStatus === 'pending' ? 'Link sent to customer • Awaiting payment' :
                 createdBooking.paymentStatus === 'unpaid' ? 'Payment to be collected later' :
                 'Customer added to Relations'}
              </p>
            </div>

            {/* Booking Details */}
            <div className="p-6 space-y-4">
              {/* Confirmation Code */}
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200 rounded-xl p-5 text-center">
                <p className="text-xs text-gray-500 uppercase tracking-widest mb-2 font-medium">Confirmation Code</p>
                <p className="text-3xl font-mono font-bold text-gray-900 tracking-wider">{createdBooking.confirmationCode}</p>
              </div>

              {/* Key Details Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1 font-medium">Customer</p>
                  <p className="text-sm font-semibold text-gray-900">{createdBooking.customerName}</p>
                  {createdBooking.customerEmail && (
                    <p className="text-xs text-gray-600 flex items-center gap-1 mt-1">
                      <Mail className="w-3 h-3" />
                      {createdBooking.customerEmail}
                    </p>
                  )}
                  {createdBooking.customerPhone && (
                    <p className="text-xs text-gray-600 flex items-center gap-1 mt-0.5">
                      <Phone className="w-3 h-3" />
                      {createdBooking.customerPhone}
                    </p>
                  )}
                </div>

                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1 font-medium">Date & Time</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {(() => {
                      const dateStr = createdBooking.preferredDate || createdBooking.date;
                      if (!dateStr) return 'Date not available';
                      const [year, month, day] = dateStr.split('-').map(Number);
                      const dateObj = new Date(year, month - 1, day);
                      return dateObj.toLocaleDateString('en-US', { 
                        month: 'short', 
                        day: 'numeric',
                        year: 'numeric'
                      });
                    })()}
                  </p>
                  <p className="text-sm text-gray-900">
                    {createdBooking.preferredTime || createdBooking.startTime} <span className="text-gray-500">{getTimezoneAbbreviation(createdBooking.timezone || orgTimezone)}</span>
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">{createdBooking.duration || 60} min</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1 font-medium">Party Size</p>
                  <p className="text-sm font-semibold text-gray-900">{createdBooking.groupSize} {createdBooking.groupSize === 1 ? 'guest' : 'guests'}</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1 font-medium">Asset</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {createdBooking.asset?.name || <span className="text-gray-400 italic">Not assigned</span>}
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1 font-medium">Team</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {createdBooking.teamAssignments?.length > 0 
                      ? `${createdBooking.teamAssignments.length} assigned` 
                      : <span className="text-gray-400 italic">Not assigned</span>}
                  </p>
                </div>
              </div>

              {/* Payment Summary */}
              {createdBooking.totalAmount > 0 && (
                <div className={`rounded-xl p-4 ${
                  createdBooking.paymentStatus === 'paid' ? 'bg-emerald-50 border border-emerald-200' :
                  createdBooking.paymentStatus === 'pending' ? 'bg-blue-50 border border-blue-200' :
                  'bg-amber-50 border border-amber-200'
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-xs uppercase tracking-wide font-medium ${
                      createdBooking.paymentStatus === 'paid' ? 'text-emerald-700' :
                      createdBooking.paymentStatus === 'pending' ? 'text-blue-700' :
                      'text-amber-700'
                    }`}>
                      {createdBooking.paymentStatus === 'paid' ? '✓ Payment Received' :
                       createdBooking.paymentStatus === 'pending' ? '⏳ Awaiting Payment' :
                       '⚡ Pay Later'}
                    </span>
                  </div>
                  {(() => {
                    const subtotal = parseFloat(createdBooking.totalAmount);
                    const processingFee = (subtotal * 0.059) + 0.30;
                    const customerPays = subtotal + processingFee;
                    const currency = createdBooking.currency || selectedService?.currency || 'USD';
                    
                    return (
                      <div className="space-y-1">
                        <div className="flex justify-between text-sm text-gray-600">
                          <span>Subtotal</span>
                          <span>{formatCurrency(subtotal, currency)}</span>
                        </div>
                        <div className="flex justify-between text-sm text-gray-600">
                          <span>Processing Fee</span>
                          <span>+{formatCurrency(processingFee, currency)}</span>
                        </div>
                        <div className={`flex justify-between text-lg font-bold pt-2 border-t ${
                          createdBooking.paymentStatus === 'paid' ? 'text-emerald-700 border-emerald-200' :
                          createdBooking.paymentStatus === 'pending' ? 'text-blue-700 border-blue-200' :
                          'text-amber-700 border-amber-200'
                        }`}>
                          <span>Total</span>
                          <span>{formatCurrency(customerPays, currency)}</span>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Action Buttons */}
              <div className="space-y-2 pt-2">
                {/* Copy Link Button */}
                <Button
                  variant="outline"
                  onClick={async () => {
                    try {
                      const response = await api.post(`/public/bookings/${createdBooking.confirmationCode}/generate-magic-link`);
                      const magicToken = response.data?.magicToken;
                      const link = magicToken 
                        ? `${window.location.origin}/customer-ticket/${createdBooking.confirmationCode}?token=${magicToken}`
                        : `${window.location.origin}/customer-ticket/${createdBooking.confirmationCode}`;
                      navigator.clipboard.writeText(link);
                      toast.success('Ticket link copied!');
                    } catch (err) {
                      const link = `${window.location.origin}/customer-ticket/${createdBooking.confirmationCode}`;
                      navigator.clipboard.writeText(link);
                      toast.success('Ticket link copied!');
                    }
                  }}
                  className="w-full"
                >
                  <Link2 className="w-4 h-4 mr-2" />
                  Copy Ticket Link
                </Button>

                {/* Add to Calendar Button */}
                <Button
                  variant="outline"
                  onClick={() => {
                    // Use webcal for automatic calendar subscription
                    const baseUrl = BACKEND_URL.replace('http://', '').replace('https://', '');
                    const calendarUrl = `webcal://${baseUrl}/api/customer/${createdBooking.confirmationCode}/calendar-feed.ics`;
                    window.location.href = calendarUrl;
                    toast.success('Opening calendar app to sync your booking...');
                  }}
                  className="w-full"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Sync to Calendar
                </Button>

                {/* Show email/sms buttons if contact info provided */}
                {(createdBooking.customerEmail || createdBooking.customerPhone) && (
                  <div className="grid grid-cols-2 gap-2">
                    {createdBooking.customerEmail && (
                      <Button
                        variant="outline"
                        onClick={async () => {
                          try {
                            // Generate magic link first
                            let ticketLink = `${window.location.origin}/customer-ticket/${createdBooking.confirmationCode}`;
                            try {
                              const magicResponse = await api.post(`/public/bookings/${createdBooking.confirmationCode}/generate-magic-link`);
                              if (magicResponse.data?.magicToken) {
                                ticketLink += `?token=${magicResponse.data.magicToken}`;
                              }
                            } catch {}
                            
                            // Use preferredDate consistently (not 'date' which may not exist)
                            const bookingDate = createdBooking.preferredDate || createdBooking.date;
                            const formattedDate = bookingDate ? new Date(bookingDate + 'T00:00:00').toLocaleDateString() : 'TBD';
                            await api.post('/emails/send-to-customer', {
                              email: createdBooking.customerEmail,
                              name: createdBooking.customerName,
                              subject: `Your Booking Confirmation - ${createdBooking.confirmationCode}`,
                              message: `Hi ${createdBooking.customerName},\n\nYour booking is confirmed!\n\nConfirmation Code: ${createdBooking.confirmationCode}\nDate: ${formattedDate}\nTime: ${createdBooking.startTime} ${getTimezoneAbbreviation(orgTimezone)}\n\nView your ticket: ${ticketLink}\n\nSee you soon!`
                            });
                            toast.success('Email sent with magic link!');
                          } catch (error) {
                            toast.error('Failed to send email');
                          }
                        }}
                        className="flex-1"
                      >
                        <Mail className="w-4 h-4 mr-2" />
                        Email
                      </Button>
                    )}
                    {createdBooking.customerPhone && (
                      <Button
                        variant="outline"
                        disabled
                        className="flex-1 opacity-50 cursor-not-allowed"
                        title="SMS requires A2P 10DLC verification"
                      >
                        <Phone className="w-4 h-4 mr-2" />
                        SMS (Coming Soon)
                      </Button>
                    )}
                  </div>
                )}
              </div>

              <Button
                onClick={async () => {
                  
                  setShowSuccessModal(false);
                  onCancel(); // Close AddBookingWizard
                  
                  // Trigger data refresh in parent CalendarScheduler
                  if (window.refreshBookingData) {
                    await window.refreshBookingData();
                  }
                  
                  toast.success('Booking created successfully!');
                }}
                className="w-full bg-blue-600 hover:bg-blue-700 transition-all duration-200"
              >
                Done
              </Button>
            </div>
          </div>
        </div>
        </>
      )}

      {/* Share Payment Link Modal */}
      {showShareLinkModal && paymentLinkData && (
        <SharePaymentLinkModal
          open={showShareLinkModal}
          onClose={() => {
            setShowShareLinkModal(false);
            onComplete(); // Close wizard and refresh bookings
          }}
          checkoutUrl={paymentLinkData.checkoutUrl}
          customerEmail={paymentLinkData.customerEmail}
          customerPhone={paymentLinkData.customerPhone}
          bookingDetails={paymentLinkData.bookingDetails}
        />
      )}
    </div>
    </>
  );
};

export default AddBookingWizard;
